import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import {
  FaSync,
  FaTrash,
  FaRedo,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaLayerGroup,
  FaEye,
  FaRocket,
  FaBoxes,
} from "react-icons/fa";

const getAdminToken = () => localStorage.getItem("admin_token");
const adminHeaders = () => ({
  headers: { Authorization: `Bearer ${getAdminToken()}` },
});

const TemplatePoolManager = () => {
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [templatesPerCondition, setTemplatesPerCondition] = useState(5);

  // Load studies
  useEffect(() => {
    const loadStudies = async () => {
      try {
        const { data } = await api.get("/admin/studies", adminHeaders());
        setStudies(data);
        if (data.length > 0 && !selectedStudy) {
          setSelectedStudy(data[0]._id);
        }
      } catch (e) {
        console.error("Failed to load studies:", e);
      }
    };
    loadStudies();
  }, []);

  // Load templates for selected study
  const loadTemplates = useCallback(async () => {
    if (!selectedStudy) return;
    try {
      const { data } = await api.get(`/admin/template-pool/${selectedStudy}`, adminHeaders());
      setTemplates(data.templates || []);
    } catch (e) {
      console.error("Failed to load templates:", e);
    }
  }, [selectedStudy]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Poll for status updates while generating
  useEffect(() => {
    if (generating) {
      const interval = setInterval(loadTemplates, 5000);
      setPollInterval(interval);
      return () => clearInterval(interval);
    } else if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  }, [generating, loadTemplates]);

  // Check if generation is complete
  useEffect(() => {
    if (generating && templates.length > 0) {
      const allDone = templates.every((t) => t.status === "ready" || t.status === "error");
      if (allDone) {
        setGenerating(false);
      }
    }
  }, [templates, generating]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      await api.post(
        "/admin/template-pool/generate",
        { studyId: selectedStudy, templatesPerCondition },
        adminHeaders()
      );
      setTimeout(loadTemplates, 2000);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setGenerating(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template and all its data?")) return;
    try {
      await api.delete(`/admin/template-pool/${templateId}`, adminHeaders());
      loadTemplates();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL ${templates.length} templates for this study? This stops any ongoing generation.`)) return;
    try {
      await api.delete(`/admin/template-pool/study/${selectedStudy}`, adminHeaders());
      setGenerating(false);
      setTemplates([]);
      loadTemplates();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const handleExport = async () => {
    try {
      const { data } = await api.get(
        `/admin/template-pool/${selectedStudy}/export`,
        adminHeaders()
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `templates-${selectedStudy}-${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // Reset input so same file can be re-selected

    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      if (!importData.templates || !Array.isArray(importData.templates)) {
        setError("Invalid template file: missing templates array");
        return;
      }

      const targetStudy = selectedStudy;
      if (!window.confirm(
        `Import ${importData.templates.length} templates into study "${targetStudy}"?\n\n` +
        `Existing templates with the same condition + index will be skipped.`
      )) return;

      const { data } = await api.post(
        "/admin/template-pool/import",
        { studyId: targetStudy, templates: importData.templates },
        adminHeaders()
      );

      alert(data.message);
      loadTemplates();
    } catch (e) {
      if (e instanceof SyntaxError) {
        setError("Invalid JSON file");
      } else {
        setError(e.response?.data?.message || e.message);
      }
    }
  };

  const handleRegenerate = async (templateId) => {
    try {
      await api.post(`/admin/template-pool/${templateId}/regenerate`, {}, adminHeaders());
      setGenerating(true);
      setTimeout(loadTemplates, 2000);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  // ─── Warm Pool ───
  const [warmPool, setWarmPool] = useState(null);
  const [warmPoolLoading, setWarmPoolLoading] = useState(false);
  const [warmPoolGenerating, setWarmPoolGenerating] = useState(false);
  const [warmPoolCount, setWarmPoolCount] = useState(70);

  const loadWarmPool = useCallback(async () => {
    if (!selectedStudy) return;
    try {
      const { data } = await api.get(`/admin/warm-pool/${selectedStudy}`, adminHeaders());
      setWarmPool(data.conditions || []);

      // Check if still generating
      const anyGenerating = (data.conditions || []).some((c) => c.generating > 0);
      if (!anyGenerating && warmPoolGenerating) {
        setWarmPoolGenerating(false);
      }
    } catch (e) {
      console.error("Failed to load warm pool:", e);
    }
  }, [selectedStudy, warmPoolGenerating]);

  useEffect(() => {
    if (selectedStudy) loadWarmPool();
  }, [selectedStudy, loadWarmPool]);

  // Poll warm pool status while generating
  useEffect(() => {
    if (warmPoolGenerating) {
      const interval = setInterval(loadWarmPool, 5000);
      return () => clearInterval(interval);
    }
  }, [warmPoolGenerating, loadWarmPool]);

  const handleWarmPoolGenerate = async () => {
    setWarmPoolGenerating(true);
    setError(null);
    try {
      await api.post(
        "/admin/warm-pool/generate",
        { studyId: selectedStudy, instancesPerCondition: warmPoolCount },
        adminHeaders()
      );
      setTimeout(loadWarmPool, 3000);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setWarmPoolGenerating(false);
    }
  };

  const handleWarmPoolClear = async () => {
    if (!window.confirm("Delete all unclaimed warm pool instances? This cannot be undone.")) return;
    try {
      const { data } = await api.delete(`/admin/warm-pool/${selectedStudy}`, adminHeaders());
      alert(data.message);
      loadWarmPool();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const warmPoolTotal = warmPool ? warmPool.reduce((sum, c) => sum + c.available, 0) : 0;
  const warmPoolClaimed = warmPool ? warmPool.reduce((sum, c) => sum + c.claimed, 0) : 0;
  const warmPoolGeneratingCount = warmPool ? warmPool.reduce((sum, c) => sum + c.generating, 0) : 0;
  const warmPoolErrors = warmPool ? warmPool.reduce((sum, c) => sum + c.errored, 0) : 0;

  // Group templates by condition
  const byCondition = {};
  templates.forEach((t) => {
    if (!byCondition[t.conditionId]) byCondition[t.conditionId] = [];
    byCondition[t.conditionId].push(t);
  });

  const totalReady = templates.filter((t) => t.status === "ready").length;
  const totalTemplates = templates.length;
  const totalGenerating = templates.filter((t) => t.status === "populating" || t.status === "initializing").length;
  const totalError = templates.filter((t) => t.status === "error").length;

  const statusColor = totalReady === totalTemplates && totalTemplates > 0
    ? "text-green-400"
    : totalError > 0
    ? "text-red-400"
    : "text-yellow-400";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-spiders-text-primary flex items-center gap-2">
          <FaLayerGroup className="text-spiders-accent-400" />
          Template Pool
        </h2>
        <button onClick={loadTemplates} className="text-spiders-text-muted hover:text-spiders-text-primary p-2">
          <FaSync className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-300 hover:text-red-100">
            <FaTimes className="w-3 h-3 inline" />
          </button>
        </div>
      )}

      {/* Study selector */}
      <div className="flex items-center gap-4">
        <select
          value={selectedStudy || ""}
          onChange={(e) => setSelectedStudy(e.target.value)}
          className="bg-spiders-bg-elevated border border-spiders-border rounded-lg px-4 py-2 text-sm text-spiders-text-primary"
        >
          {studies.map((s) => (
            <option key={s._id} value={s._id}>
              {s.name || s._id}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-xs text-spiders-text-muted">Templates per condition:</label>
          <input
            type="number"
            min={1}
            max={20}
            value={templatesPerCondition}
            onChange={(e) => setTemplatesPerCondition(parseInt(e.target.value) || 5)}
            className="w-16 bg-spiders-bg-elevated border border-spiders-border rounded px-2 py-1 text-sm text-spiders-text-primary text-center"
          />
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating || !selectedStudy}
          className="spiders-btn-primary text-sm py-2 px-4 flex items-center gap-2 rounded-lg disabled:opacity-50"
        >
          {generating ? (
            <><FaSpinner className="animate-spin w-3.5 h-3.5" /> Generating...</>
          ) : (
            <><FaRocket className="w-3.5 h-3.5" /> Generate Templates</>
          )}
        </button>

        {totalTemplates > 0 && (
          <>
            <button
              onClick={handleExport}
              className="text-sm py-2 px-4 flex items-center gap-2 rounded-lg border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 transition-colors"
            >
              <FaLayerGroup className="w-3.5 h-3.5" /> Export
            </button>
            <button
              onClick={handleDeleteAll}
              className="text-sm py-2 px-4 flex items-center gap-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <FaTrash className="w-3.5 h-3.5" /> Clear All
            </button>
          </>
        )}

        <label className="text-sm py-2 px-4 flex items-center gap-2 rounded-lg border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-colors cursor-pointer">
          <FaLayerGroup className="w-3.5 h-3.5" /> Import
          <input
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImport}
          />
        </label>
      </div>

      {/* Pool status summary */}
      {totalTemplates > 0 && (
        <div className="flex items-center gap-4 p-4 rounded-xl bg-spiders-bg-surface border border-spiders-border">
          <div className={`text-2xl font-bold ${statusColor}`}>
            {totalReady}/{totalTemplates}
          </div>
          <div className="flex-1">
            <div className="text-sm text-spiders-text-primary font-medium">
              {totalReady === totalTemplates ? "Pool Ready — study can launch" : "Templates in progress"}
            </div>
            <div className="text-xs text-spiders-text-muted mt-0.5">
              {totalReady} ready
              {totalGenerating > 0 && `, ${totalGenerating} generating`}
              {totalError > 0 && `, ${totalError} failed`}
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-48 h-2 bg-spiders-bg-elevated rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-500"
              style={{ width: `${(totalReady / totalTemplates) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Templates by condition */}
      {Object.entries(byCondition).sort(([a], [b]) => a.localeCompare(b)).map(([conditionId, condTemplates]) => {
        const condReady = condTemplates.filter((t) => t.status === "ready").length;
        // Extract readable condition name
        const condLabel = conditionId.split("__").pop()?.replace(/_/g, " ").replace("aiem ", "") || conditionId;

        return (
          <div key={conditionId} className="rounded-xl border border-spiders-border overflow-hidden">
            <div className="px-4 py-3 bg-spiders-bg-elevated flex items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-spiders-text-primary capitalize">{condLabel}</span>
                <span className="text-xs text-spiders-text-muted ml-2">
                  {condReady}/{condTemplates.length} ready
                </span>
              </div>
            </div>

            <div className="divide-y divide-spiders-border/30">
              {condTemplates.sort((a, b) => (a.templateIndex || 0) - (b.templateIndex || 0)).map((t) => (
                <div key={t._id} className="px-4 py-3 flex items-center gap-4">
                  {/* Status icon */}
                  <div className="flex-shrink-0">
                    {t.status === "ready" ? (
                      <FaCheck className="w-4 h-4 text-green-400" />
                    ) : t.status === "error" ? (
                      <FaTimes className="w-4 h-4 text-red-400" />
                    ) : (
                      <FaSpinner className="w-4 h-4 text-yellow-400 animate-spin" />
                    )}
                  </div>

                  {/* Template info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-spiders-text-primary">
                      Template #{t.templateIndex || "?"}
                      <span className="text-spiders-text-muted ml-2 text-xs">{t._id.slice(0, 8)}…</span>
                    </div>
                    <div className="text-xs text-spiders-text-muted mt-0.5">
                      {t.status === "ready" ? (
                        `${t.agentCount} agents · ${t.postCount} posts · ${t.commentCount} comments`
                      ) : t.status === "error" ? (
                        <span className="text-red-400">{t.statusMessage || "Population failed"}</span>
                      ) : (
                        "Populating..."
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {t.status === "error" && (
                      <button
                        onClick={() => handleRegenerate(t._id)}
                        className="p-2 text-spiders-text-muted hover:text-spiders-accent-400 transition-colors"
                        title="Regenerate"
                      >
                        <FaRedo className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(t._id)}
                      className="p-2 text-spiders-text-muted hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {totalTemplates === 0 && !generating && (
        <div className="text-center py-12 text-spiders-text-muted">
          <FaLayerGroup className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No templates generated yet</p>
          <p className="text-xs mt-1">Select a study and click "Generate Templates" to create the pool</p>
        </div>
      )}

      {/* ─── Warm Pool Section ─── */}
      {totalReady > 0 && (
        <div className="space-y-4 pt-6 border-t border-spiders-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-spiders-text-primary flex items-center gap-2">
              <FaBoxes className="text-blue-400" />
              Warm Pool
            </h2>
            <button onClick={loadWarmPool} className="text-spiders-text-muted hover:text-spiders-text-primary p-2">
              <FaSync className="w-4 h-4" />
            </button>
          </div>

          <p className="text-xs text-spiders-text-muted">
            Pre-create instances from templates so participant arrival is instant (~5ms instead of ~25s).
            Each instance is a ready-to-use copy. When a participant arrives, they get one immediately.
          </p>

          {/* Controls */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-xs text-spiders-text-muted">Instances per condition:</label>
              <input
                type="number"
                min={1}
                max={200}
                value={warmPoolCount}
                onChange={(e) => setWarmPoolCount(parseInt(e.target.value) || 70)}
                className="w-20 bg-spiders-bg-elevated border border-spiders-border rounded px-2 py-1 text-sm text-spiders-text-primary text-center"
              />
            </div>

            <button
              onClick={handleWarmPoolGenerate}
              disabled={warmPoolGenerating}
              className="text-sm py-2 px-4 flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
            >
              {warmPoolGenerating ? (
                <><FaSpinner className="animate-spin w-3.5 h-3.5" /> Generating...</>
              ) : (
                <><FaBoxes className="w-3.5 h-3.5" /> Generate Warm Pool</>
              )}
            </button>

            {warmPoolTotal > 0 && (
              <button
                onClick={handleWarmPoolClear}
                className="text-sm py-2 px-4 flex items-center gap-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <FaTrash className="w-3.5 h-3.5" /> Clear Unclaimed
              </button>
            )}
          </div>

          {/* Status summary */}
          {warmPool && warmPool.length > 0 && (
            <div className="flex items-center gap-4 p-4 rounded-xl bg-spiders-bg-surface border border-spiders-border">
              <div className={`text-2xl font-bold ${warmPoolTotal > 0 ? "text-blue-400" : "text-spiders-text-muted"}`}>
                {warmPoolTotal}
              </div>
              <div className="flex-1">
                <div className="text-sm text-spiders-text-primary font-medium">
                  {warmPoolTotal > 0
                    ? `${warmPoolTotal} instances ready for instant assignment`
                    : "No warm pool instances"}
                </div>
                <div className="text-xs text-spiders-text-muted mt-0.5">
                  {warmPoolTotal} available
                  {warmPoolClaimed > 0 && `, ${warmPoolClaimed} claimed by participants`}
                  {warmPoolGeneratingCount > 0 && `, ${warmPoolGeneratingCount} generating`}
                  {warmPoolErrors > 0 && `, ${warmPoolErrors} failed`}
                </div>
              </div>
            </div>
          )}

          {/* Per-condition breakdown */}
          {warmPool && warmPool.length > 0 && (
            <div className="rounded-xl border border-spiders-border overflow-hidden">
              <div className="px-4 py-3 bg-spiders-bg-elevated">
                <span className="text-sm font-semibold text-spiders-text-primary">Per-condition status</span>
              </div>
              <div className="divide-y divide-spiders-border/30">
                {warmPool.map((cond) => {
                  const condLabel = cond.conditionId.split("__").pop()?.replace(/_/g, " ").replace("aiem ", "") || cond.conditionId;
                  const total = cond.available + cond.claimed + cond.generating + cond.errored;
                  const pct = total > 0 ? (cond.available / warmPoolCount) * 100 : 0;

                  return (
                    <div key={cond.conditionId} className="px-4 py-3 flex items-center gap-4">
                      <div className="w-32 text-sm text-spiders-text-primary capitalize truncate" title={cond.conditionId}>
                        {condLabel}
                      </div>
                      {/* Progress bar */}
                      <div className="flex-1 h-2 bg-spiders-bg-elevated rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                      <div className="text-xs text-spiders-text-muted w-40 text-right">
                        <span className="text-blue-400 font-medium">{cond.available}</span> ready
                        {cond.claimed > 0 && <>, <span className="text-green-400">{cond.claimed}</span> claimed</>}
                        {cond.generating > 0 && <>, <span className="text-yellow-400">{cond.generating}</span> creating</>}
                        {cond.errored > 0 && <>, <span className="text-red-400">{cond.errored}</span> failed</>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplatePoolManager;
